/**
 * Player license level name layouts from Figma Group 1321317994 (188:9318).
 *
 * Absolute canvas coords → relative to `link_wind_cloud` button (104:24609)
 * at left 345.358, top 26.
 *
 * Most tiers share 233×82; shorter two-word titles use a narrower box,
 * still centered on the same plaque midpoint.
 *
 * `lines` — Figma text-box wrap inside that width. When omitted, each word
 * is its own line (matches all current two-word Figma titles).
 */
export type PlayerLevelNameLayout = {
  left: number;
  top: number;
  width: number;
  height: number;
  lines?: readonly string[];
};

const BUTTON_LEFT = 345.358;
const BUTTON_TOP = 26;

const DEFAULT_LAYOUT: PlayerLevelNameLayout = {
  left: 423.8583984375 - BUTTON_LEFT,
  top: 33.90625 - BUTTON_TOP,
  width: 233,
  height: 82,
};

/** Figma 188:9307…188:9316 — keyed by display label. */
export const PLAYER_LEVEL_NAME_LAYOUTS: Record<string, PlayerLevelNameLayout> = {
  // Level 0 single-line title sits higher optically; nudge down to center on plaque.
  // Ученик: {
  //   ...DEFAULT_LAYOUT,
  //   top: DEFAULT_LAYOUT.top + 20,
  // },
  'Тучка на побегушках': {
    ...DEFAULT_LAYOUT,
    // Figma 188:9307 wraps as two lines inside 233×82 (not one word per line).
    lines: ['Тучка на', 'побегушках'],
  },
  'Ветреное облако': DEFAULT_LAYOUT,
  'Туманный специалист': DEFAULT_LAYOUT,
  'Блуждающий огонёк': DEFAULT_LAYOUT,
  'Улётный красавчик': DEFAULT_LAYOUT,
  'Звёздный курьер': DEFAULT_LAYOUT,
  'Властелин посылок': DEFAULT_LAYOUT,
  'Гроза заказов': {
    left: 458.65625 - BUTTON_LEFT,
    top: 33.90625 - BUTTON_TOP,
    width: 163.404296875,
    height: 82,
  },
  'Мастер полётов': {
    left: 457.8671875 - BUTTON_LEFT,
    top: 33.90625 - BUTTON_TOP,
    width: 164.982421875,
    height: 82,
  },
  'Легенда доставки': DEFAULT_LAYOUT,
};

export function getPlayerLevelNameLayout(name: string): PlayerLevelNameLayout {
  return PLAYER_LEVEL_NAME_LAYOUTS[name] ?? DEFAULT_LAYOUT;
}

/** Resolve display lines: explicit Figma wrap → `\n` → one word per line. */
export function getPlayerLevelNameLines(
  name: string,
  layout: PlayerLevelNameLayout = getPlayerLevelNameLayout(name),
): string[] {
  if (layout.lines && layout.lines.length > 0) {
    return [...layout.lines];
  }
  if (name.includes('\n')) {
    return name.split('\n');
  }
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words.length > 0 ? words : [name];
}
